import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { sourcingAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Info } from 'lucide-react';
import { TIER_BADGE, badgeClass } from './sourcingUtils';

const TIER_FILTERS = [
  { value: '', label: 'All' },
  { value: 'HOT', label: 'Hot' },
  { value: 'WATCH', label: 'Watch' },
  { value: 'NORMAL', label: 'Normal' },
];

export default function HotListTab() {
  const { canAccess } = useAuth();
  const canEdit = canAccess('SOURCING', 'EDIT');
  const [loading, setLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [data, setData] = useState({ parts: [], meta: {}, counts: {} });
  const [settings, setSettings] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  // Hot List tier filter round (3 Nov 2026): remembered per browser.
  const [tierFilter, setTierFilter] = useState(() => {
    try { return localStorage.getItem('sourcing_hot_tier_filter') || ''; } catch { return ''; }
  });

  const load = (tier = tierFilter) => {
    setFiltering(true);
    sourcingAPI.hotList(tier ? { tier } : {})
      .then((res) => setData(res.data))
      .catch(() => toast.error('Failed to load Hot List'))
      .finally(() => { setLoading(false); setFiltering(false); });
  };

  useEffect(() => { load(tierFilter); }, [tierFilter]);
  useEffect(() => {
    sourcingAPI.getSettings().then((res) => setSettings(res.data)).catch(() => {});
  }, []);

  const changeTierFilter = (v) => {
    setTierFilter(v);
    try { localStorage.setItem('sourcing_hot_tier_filter', v); } catch { /* ignore */ }
  };

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const res = await sourcingAPI.rebuildHot();
      const { tiers_computed, unmatched_count } = res.data;
      toast.success(`Hot List rebuilt — ${tiers_computed} SKU${tiers_computed === 1 ? '' : 's'} computed, ${unmatched_count} unmatched`, {
        id: 'hot-list-rebuild-result',
      });
      load();
    } catch (e) {
      toast.error('Failed to rebuild');
    } finally {
      setRebuilding(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16" data-testid="hot-list-loading"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="sourcing-hot-list-tab">
      {canEdit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {data.meta?.unmatched_count != null && `${data.meta.unmatched_count} SO lines (last 4 months) unmatched to a SKU.`}
          </p>
          <Button variant="outline" size="sm" onClick={rebuild} disabled={rebuilding} data-testid="hot-list-rebuild-btn">
            {rebuilding ? 'Rebuilding...' : 'Rebuild Now'}
          </Button>
        </div>
      )}
      {canEdit && data.meta?.monthly_so_counts && (
        <Card className="rounded-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">SO Lines by Month</CardTitle></CardHeader>
          <CardContent className="flex gap-4 flex-wrap">
            {Object.entries(data.meta.monthly_so_counts).map(([m, c]) => (
              <div key={m} className="text-sm"><span className="text-muted-foreground">{m}:</span> <span className="font-medium">{c}</span></div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-2" data-testid="hot-list-tier-filter">
        {TIER_FILTERS.map((f) => (
          <button
            key={f.value || 'ALL'}
            onClick={() => changeTierFilter(f.value)}
            data-testid={`hot-list-tier-filter-${(f.value || 'all').toLowerCase()}`}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              tierFilter === f.value ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:border-foreground/40'
            }`}
          >
            {f.label}{f.value ? ` (${data.counts?.[f.value] ?? 0})` : ` (${Object.values(data.counts || {}).reduce((a, b) => a + b, 0)})`}
          </button>
        ))}
      </div>

      {settings && (
        <Card className="rounded-sm bg-muted/20" data-testid="hot-list-how-tiers-work">
          <CardContent className="p-3 text-xs text-muted-foreground flex items-start gap-2">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              Over the last {settings.hot_period_days} days: HOT needs 2+ of — sales up {settings.hot_sales_change_pct}%+,
              {' '}{settings.hot_min_customers}+ customers, {settings.hot_min_offers}+ vendor offers in Bid Engine —
              AND stock covering {settings.hot_cover_days} days or less. WATCH is exactly 1 signal. NORMAL is otherwise.
            </span>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm">
        <CardContent className="p-0 divide-y divide-border">
          {filtering && (
            <div className="p-8 flex justify-center" data-testid="hot-list-filtering"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          )}
          {!filtering && data.parts.map((p) => (
            <div key={p.sku_id} className="p-3 flex items-center justify-between gap-3 text-sm flex-wrap" data-testid={`hot-list-row-${p.sku_id}`}>
              <span className="font-mono">{p.sku_id}</span>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs text-muted-foreground" data-testid={`hot-list-signals-${p.sku_id}`}>
                  Sales {p.sales_change_pct != null ? `${p.sales_change_pct}%` : '—'} · {p.distinct_customers ?? '—'} customers ·
                  {' '}{p.offer_count ?? '—'} offers · cover {p.cover_days != null ? `${p.cover_days}d` : '—'}
                </span>
                {p.bb_searches != null && (
                  <span className="text-xs text-muted-foreground" data-testid={`hot-list-bb-searches-${p.sku_id}`}>
                    {p.bb_searches} BrokerBin searches / week{p.bb_avg_qty != null ? ` · avg qty ${p.bb_avg_qty}` : ''}
                  </span>
                )}
                <Badge variant="outline" className={badgeClass(TIER_BADGE, p.tier)}>{p.tier}</Badge>
              </div>
            </div>
          ))}
          {!filtering && data.parts.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {tierFilter ? `No ${tierFilter.charAt(0)}${tierFilter.slice(1).toLowerCase()} SKUs right now.` : 'No Hot/Watch SKUs yet — rebuild to compute from recent sales demand.'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
